const SUB = (name, plural = "empresas") =>
  `Soy Carlos Garcín, Professional EOS Implementer® certificado por EOS Worldwide. Trabajo con empresarios y equipos de liderazgo en ${name} para alinear su visión, ejecutar con disciplina y construir ${plural} que dependan menos del dueño para avanzar.`;

const SEO = (name) => ({
  title: `EOS en ${name} | Implementador EOS ${name} | Carlos Garcín`,
  description: `Implementa EOS en ${name} con Carlos Garcín, Professional EOS Implementer®. Alinea a tu equipo, mejora la ejecución y construye una empresa que dependa menos de ti.`,
  keywords: `EOS ${name}, EOS en ${name}, implementador EOS ${name}, implementación EOS ${name}, Professional EOS Implementer ${name}, Entrepreneurial Operating System ${name}`,
});

const IMPL = (name) =>
  `Sí. Carlos Garcín es Professional EOS Implementer® certificado por EOS Worldwide y trabaja con empresas de ${name}, de forma presencial y virtual.`;

export const CHILE = {
  key: "chile",
  name: "Chile",
  path: "/eos-en-chile",
  locale: { lang: "es-CL", og: "es_CL" },
  seo: SEO("Chile"),
  heroSubtitle:
    "Soy Carlos Garcín, Professional EOS Implementer® certificado por EOS Worldwide. Trabajo con empresarios y equipos de liderazgo en Chile para alinear su visión, ejecutar con disciplina y construir una empresa que dependa menos del dueño para avanzar.",
  context: {
    titlePlain: "Tu empresa puede seguir creciendo",
    titleOrange: "sin depender cada vez más de ti.",
    paragraphs: [
      "Muchas empresas chilenas llegan a una etapa en que el negocio crece más rápido que la forma de gestionarlo. Lo que antes podía resolver directamente el fundador o gerente general comienza a necesitar un **verdadero equipo de liderazgo**, prioridades compartidas y responsabilidades claras.",
      "El desafío es **profesionalizar la gestión sin llenar la empresa de burocracia**. EOS entrega un sistema simple para alinear al equipo, ejecutar prioridades y resolver problemas con mayor disciplina.",
    ],
  },
  fitClosing:
    "EOS no depende de una industria específica. Puede aplicarse en empresas de servicios, tecnología, industria, distribución, construcción, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer:
    "Sí. Carlos Garcín es Professional EOS Implementer® certificado por EOS Worldwide y está basado en Chile.",
  locationFaq: {
    q: "¿Trabajas solamente con empresas de Santiago?",
    a: "No. Estoy basado en Santiago, pero trabajo con empresas de todo Chile. Puedo facilitar sesiones presenciales tanto en Santiago como en otras ciudades del país, y también sesiones virtuales para equipos que prefieran esa modalidad. Ambas formas de trabajo son parte habitual de una implementación de EOS.",
  },
  finalCtaSuffix: "",
};

export const PERU = {
  key: "peru",
  name: "Perú",
  path: "/eos-peru",
  locale: { lang: "es-PE", og: "es_PE" },
  seo: SEO("Perú"),
  heroSubtitle: SUB("Perú"),
  context: {
    titlePlain: "Tu empresa puede seguir creciendo",
    titleOrange: "sin que cada nueva etapa dependa más de ti.",
    paragraphs: [
      "Muchas empresas peruanas crecen sumando personas, áreas, clientes y operaciones, principalmente desde Lima pero también expandiéndose hacia ciudades como Arequipa, Trujillo o Piura. El desafío aparece cuando ese crecimiento aumenta la complejidad, pero **las decisiones importantes siguen concentrándose en el fundador o gerente general**.",
      "EOS ayuda al equipo de liderazgo a **ordenar esa siguiente etapa**: alinear la visión, definir responsabilidades, establecer prioridades claras y construir una disciplina de ejecución que permita seguir creciendo sin agregar burocracia innecesaria.",
    ],
  },
  fitClosing:
    "EOS no depende de una industria específica. Puede aplicarse en empresas peruanas de servicios, tecnología, industria, distribución, construcción, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer: IMPL("Perú"),
  locationFaq: {
    q: "¿Trabajas presencialmente con empresas en Perú?",
    a: "Sí. Trabajo con empresas de Perú tanto de forma presencial como virtual. Puedo facilitar sesiones presenciales en Lima y otras ciudades del país, y también trabajar virtualmente con equipos que prefieran esa modalidad. Ambas formas de trabajo son parte habitual de una implementación de EOS.",
  },
  finalCtaSuffix: " en Perú",
};

export const COLOMBIA = {
  key: "colombia",
  name: "Colombia",
  path: "/eos-colombia",
  locale: { lang: "es-CO", og: "es_CO" },
  seo: SEO("Colombia"),
  heroSubtitle: SUB("Colombia"),
  context: {
    titlePlain: "Crecer entre equipos, áreas y ciudades",
    titleOrange: "no debería hacer tu empresa más difícil de dirigir.",
    paragraphs: [
      "Muchas empresas colombianas crecen incorporando nuevos equipos, unidades o mercados, incluso distribuyendo su operación entre ciudades como Bogotá, Medellín, Cali o Barranquilla. A medida que aumenta esa complejidad, **mantener al equipo de liderazgo alineado** y evitar que las decisiones sigan concentrándose arriba se vuelve cada vez más importante.",
      "EOS entrega al equipo de liderazgo **una forma común de dirigir la empresa**: una visión compartida, prioridades claras, responsabilidades definidas y una disciplina consistente para ejecutar y resolver problemas.",
    ],
  },
  fitClosing:
    "EOS puede aplicarse en empresas colombianas de servicios, tecnología, distribución, manufactura, construcción, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer: IMPL("Colombia"),
  locationFaq: {
    q: "¿Puedes trabajar con empresas en Bogotá, Medellín y otras ciudades de Colombia?",
    a: "Sí. Trabajo con empresas de Colombia tanto de forma presencial como virtual. Puedo facilitar sesiones presenciales en Bogotá, Medellín, Cali, Barranquilla y otras ciudades, además de trabajar virtualmente con equipos de liderazgo de todo el país.",
  },
  finalCtaSuffix: " en Colombia",
};

export const ARGENTINA = {
  key: "argentina",
  name: "Argentina",
  path: "/eos-argentina",
  locale: { lang: "es-AR", og: "es_AR" },
  seo: SEO("Argentina"),
  heroSubtitle: SUB("Argentina"),
  context: {
    titlePlain: "En un entorno que cambia constantemente,",
    titleOrange: "tu empresa necesita aún más claridad por dentro.",
    paragraphs: [
      "Dirigir una empresa en Argentina exige capacidad de adaptación, tanto en Buenos Aires como en polos empresariales como Córdoba, Rosario o Mendoza. Pero adaptarse al entorno no debería significar **cambiar prioridades constantemente**, concentrar todas las decisiones arriba o perder foco dentro del equipo de liderazgo.",
      "EOS ayuda a **fortalecer aquello que la empresa sí puede controlar**: una visión compartida, prioridades concretas, responsabilidades claras, números visibles y una disciplina de ejecución que permita responder a los cambios sin perder el rumbo.",
    ],
  },
  fitClosing:
    "EOS puede aplicarse en empresas argentinas de tecnología, servicios profesionales, industria, distribución, construcción, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer: IMPL("Argentina"),
  locationFaq: {
    q: "¿Trabajas con empresas fuera de Buenos Aires?",
    a: "Sí. Trabajo con empresas de Argentina tanto de forma presencial como virtual. Puedo facilitar sesiones presenciales en Buenos Aires, Córdoba, Rosario, Mendoza y otras ciudades, además de trabajar virtualmente con equipos de todo el país.",
  },
  finalCtaSuffix: " en Argentina",
};

export const URUGUAY = {
  key: "uruguay",
  name: "Uruguay",
  path: "/eos-uruguay",
  locale: { lang: "es-UY", og: "es_UY" },
  seo: SEO("Uruguay"),
  heroSubtitle: SUB("Uruguay"),
  context: {
    titlePlain: "Tu empresa puede seguir creciendo",
    titleOrange: "sin que todas las decisiones pasen por las mismas personas.",
    paragraphs: [
      "Muchas empresas uruguayas crecen manteniendo equipos relativamente compactos y una forma de trabajar ágil y cercana. A medida que la organización aumenta su tamaño y complejidad, especialmente entre Montevideo y otras ciudades del país, esa misma cercanía puede transformarse en **dependencia de unas pocas personas para decidir, coordinar y resolver**.",
      "EOS ayuda a **conservar la agilidad mientras la gestión evoluciona**: un equipo de liderazgo alineado, prioridades compartidas, responsabilidades claras y una forma consistente de ejecutar y resolver problemas.",
    ],
  },
  fitClosing:
    "EOS puede aplicarse en empresas uruguayas de servicios, tecnología, comercio, distribución, industria, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer: IMPL("Uruguay"),
  locationFaq: {
    q: "¿Trabajas presencialmente con empresas en Uruguay?",
    a: "Sí. Trabajo con empresas de Uruguay tanto de forma presencial como virtual. Puedo facilitar sesiones presenciales en Montevideo y otras ciudades del país, además de trabajar virtualmente con equipos que prefieran esa modalidad.",
  },
  finalCtaSuffix: " en Uruguay",
};

export const ECUADOR = {
  key: "ecuador",
  name: "Ecuador",
  path: "/eos-ecuador",
  locale: { lang: "es-EC", og: "es_EC" },
  seo: SEO("Ecuador"),
  heroSubtitle: SUB("Ecuador"),
  context: {
    titlePlain: "Tu empresa puede seguir creciendo",
    titleOrange: "sin concentrar cada vez más decisiones en el dueño.",
    paragraphs: [
      "Muchas empresas ecuatorianas crecen sumando equipos, áreas y operaciones entre mercados como Quito, Guayaquil, Cuenca y otras ciudades del país. Cuando ese crecimiento sigue dependiendo principalmente del fundador o de unas pocas personas clave, **la capacidad de la organización para avanzar comienza a encontrar un límite**.",
      "EOS ayuda a construir **una empresa menos dependiente de individuos específicos**: alinea al equipo de liderazgo, clarifica responsabilidades, define prioridades y crea una disciplina consistente para ejecutar y resolver los problemas que aparecen durante el crecimiento.",
    ],
  },
  fitClosing:
    "EOS puede aplicarse en empresas ecuatorianas de servicios, tecnología, distribución, industria, construcción, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer: IMPL("Ecuador"),
  locationFaq: {
    q: "¿Puedes trabajar con empresas en Quito, Guayaquil y otras ciudades de Ecuador?",
    a: "Sí. Trabajo con empresas de Ecuador tanto de forma presencial como virtual. Puedo facilitar sesiones presenciales en Quito, Guayaquil, Cuenca y otras ciudades, además de trabajar virtualmente con equipos de liderazgo de todo el país.",
  },
  finalCtaSuffix: " en Ecuador",
};

export const PARAGUAY = {
  key: "paraguay",
  name: "Paraguay",
  path: "/eos-paraguay",
  locale: { lang: "es-PY", og: "es_PY" },
  seo: SEO("Paraguay"),
  heroSubtitle: SUB("Paraguay"),
  context: {
    titlePlain: "Cuando una empresa crece,",
    titleOrange: "también tiene que evolucionar la forma de dirigirla.",
    paragraphs: [
      "Muchas empresas paraguayas crecen manteniendo una cultura cercana y fuertemente vinculada a sus fundadores, especialmente en mercados como Asunción, Ciudad del Este y Encarnación. Esa cercanía puede ser una gran fortaleza, pero empieza a limitar el crecimiento cuando **demasiadas decisiones y responsabilidades continúan dependiendo de unas pocas personas**.",
      "EOS ayuda a **profesionalizar la gestión sin perder el espíritu emprendedor**: clarifica responsabilidades, alinea al equipo de liderazgo y establece una disciplina de ejecución que permite seguir creciendo con mayor control, foco y autonomía.",
    ],
  },
  fitClosing:
    "EOS puede aplicarse en empresas paraguayas de servicios, distribución, construcción, industria, agronegocios, empresas familiares y otros negocios emprendedores.",
  implementerFaqAnswer: IMPL("Paraguay"),
  locationFaq: {
    q: "¿Trabajas presencialmente con empresas en Paraguay?",
    a: "Sí. Trabajo con empresas de Paraguay tanto de forma presencial como virtual. Puedo facilitar sesiones presenciales en Asunción, Ciudad del Este, Encarnación y otras ciudades, además de trabajar virtualmente con equipos de todo el país.",
  },
  finalCtaSuffix: " en Paraguay",
};
